import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Snackbar,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Collapse,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  fonts,
  getStartedModalCloseIcon,
  getStartedModalStyles,
  getStartedSmallButtons,
  getStartedTexts,
  headingFonts,
  mainButtonStyles,
  textFieldStyles,
} from "../../Styles/Styles";

export default function RegisterForm({ onClose }) {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    organization: "",
    password: "",
    ipAddress: "",
    acceptTerms: false,
    subscribeNewsletter: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showOrgField, setShowOrgField] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const handleToggle = () => {
    setShowOrgField((prev) => !prev);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);
    if (!formData.acceptTerms) {
      setError("You must accept the Terms of Service to sign up.");
      setIsLoading(false);
      return;
    }

    try {
      // Fetch IP and update a temporary formData copy
      const res = await fetch("https://api.ipify.org?format=json");
      const data = await res.json();
      const updatedFormData = { ...formData, ipAddress: data.ip };

      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/user/register`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatedFormData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Signup failed");
      }

      setSuccessMessage(
        "Your account has been created! A verification email has been sent to your inbox."
      );
      setOpenSnackbar(true);
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        organization: "",
        password: "",
        ipAddress: "",
        acceptTerms: false,
        subscribeNewsletter: false,
      });
      // Wait 2 seconds and switch to the login form
      setTimeout(() => {
        onClose("login");
      }, 5000);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ ...getStartedModalStyles }}>
      {/* Close Button */}
      <IconButton
        onClick={() => onClose("buttons")}
        sx={{ ...getStartedModalCloseIcon }}
      >
        <CancelOutlinedIcon />
      </IconButton>

      <Typography
        variant="h5"
        mb={1}
        fontWeight="bold"
        sx={{
          ...headingFonts,
          color: "var(--secondary-color)",
        }}
      >
        Register
      </Typography>

      <form onSubmit={handleSubmit}>
        {/* First Name */}
        <TextField
          label="First Name"
          name="firstName"
          value={formData.firstName}
          onChange={handleChange}
          fullWidth
          required
          margin="normal"
          sx={{ ...textFieldStyles }}
        />

        {/* Last Name */}
        <TextField
          label="Last Name"
          name="lastName"
          value={formData.lastName}
          onChange={handleChange}
          fullWidth
          required
          margin="normal"
          sx={{ ...textFieldStyles }}
        />

        {/* Email */}
        <TextField
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          fullWidth
          required
          margin="normal"
          sx={{ ...textFieldStyles }}
        />

        {/* Organisation/Company */}
        <Typography
          variant="body2"
          display="flex"
          alignItems="center"
          onClick={handleToggle}
          sx={{ cursor: "pointer" }}
        >
          Organisation/Company
          {showOrgField ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Typography>

        <Collapse in={showOrgField}>
          <TextField
            label="Organisation/Company"
            name="organization"
            type="organization"
            value={formData.organization}
            onChange={handleChange}
            fullWidth
            margin="normal"
            sx={{ mt: 2, ...textFieldStyles }}
          />
        </Collapse>

        {/* Password */}
        <TextField
          label="Password"
          name="password"
          type={showPassword ? "text" : "password"}
          value={formData.password}
          onChange={handleChange}
          fullWidth
          required
          margin="normal"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ ...textFieldStyles }}
        />

        {/* Accept Terms */}
        <FormControlLabel
          control={
            <Checkbox
              name="acceptTerms"
              checked={formData.acceptTerms}
              onChange={handleChange}
              sx={{
                "&.Mui-checked": {
                  color: "var(--primary-color)", // Filled color when checked
                },
              }}
            />
          }
          label={
            <Typography sx={{ ...fonts }}>
              I accept the Terms of Service
            </Typography>
          }
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            marginLeft: 0,
          }}
        />

        {/* Subscribe to Newsletter */}
        <FormControlLabel
          control={
            <Checkbox
              name="subscribeNewsletter"
              checked={formData.subscribeNewsletter}
              onChange={handleChange}
              sx={{
                "&.Mui-checked": {
                  color: "var(--primary-color)", // Filled color when checked
                },
              }}
            />
          }
          label={
            <Typography sx={{ ...fonts }}>
              Subscribe to our Newsletter
            </Typography>
          }
          sx={{ display: "flex", width: "100%", marginLeft: 0 }}
        />

        {/* Submit Button */}
        <Button
          type="submit"
          className="main-button-style"
          sx={{ mt: 2, ...mainButtonStyles }}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <span>Creating Account...</span>
              <CircularProgress size={20} sx={{ color: "white" }} />
            </>
          ) : (
            <>
              <span>Register</span>
              <ArrowForwardOutlinedIcon />
            </>
          )}
        </Button>
      </form>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {successMessage}
        </Alert>
      )}

      <Typography variant="body2" sx={{ mt: 2, ...getStartedTexts }}>
        Already have an account?{" "}
        <Button
          size="small"
          sx={{ ...getStartedSmallButtons }}
          onClick={() => onClose("login")}
        >
          Login Now
        </Button>
      </Typography>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert severity="success">
          Account created! Redirecting to login...
        </Alert>
      </Snackbar>
    </Box>
  );
}
